import { uid } from "@dhx/ts-common/core";
import { sv } from "@dhx/ts-common/dom";
import { IFitPosition } from "@dhx/ts-common/html";
import { DataEvents, TreeCollection } from "@dhx/ts-data";
import { getSizesSVGText, locator, roundToTwoNumAfterPoint, verticalCenteredText } from "../helpers/common";
import { ChartEvents, ITreeMapConfig, ITreeSeries, Locator, PointData, SvgElement } from "../types";
import BaseSeria from "./BaseSeria";
interface ITreeLayout {
	area: number;
	width: number;
	height: number;
	restArea: number;
	restWidth: number;
	restHeight: number;
	startX?: number;
	startY?: number;
	stroke?: number;
	cols?: ITreeLayout[];
	rows?: ITreeLayout[];
	id?: string;
	parent?: string;
}

export default class TreeMap extends BaseSeria {
	public config: ITreeMapConfig;

	protected _data: TreeCollection;

	private _sum: number;
	private _maxLevel: number;
	private _aspectRatio: number;
	private _layout: ITreeLayout;
	private _headerHeight: number;
	private _textLocator: Locator;
	private _valueLocator: Locator;

	scaleReady(sizes: IFitPosition): IFitPosition {
		for (const key in sizes) {
			sizes[key] += this.config.paddings;
		}
		return sizes;
	}
	toggle(id?: string): void {
		const serie = this.config.treeSeries.find(item => item.id === id);
		if (serie) serie.active = !serie.active;
	}
	dataReady(): PointData[] {
		if (!this.config.active) {
			return (this._points = []);
		}
		const isGruopName = this.config.legendType === "groupName";
		this._sum = 0;
		this._data.forEach(item => {
			const parent = this._data.getItem(this._data.getParent(item.id));
			if (parent && parent.$hidden && this._maxLevel > 1) return;
			const value = this._valueLocator(item);
			if (value) {
				const serie = this._getSerie(isGruopName ? item.id : value, isGruopName);
				item.color = serie ? serie.color : item.color;
				item.$hidden = serie ? !serie.active : false;
				if (!item.$hidden && value) this._sum += parseFloat(item[this.config.value]);
			}
		});

		this._points = [];
		this._data.eachChild(
			this._data.getRoot(),
			item => {
				if (item.$hidden) return;
				let value = this._valueLocator(item);
				const text = this._textLocator(item);
				const children = [];
				if (!value && this._data.haveItems(item.id)) {
					value = 0;
					this._data.eachChild(item.id, child => {
						if (child.$hidden) return;
						const childValue = this._valueLocator(child);
						const childText = this._textLocator(child);
						const childColor =
							child.color ||
							this._getSerie(isGruopName ? child.id : childValue, isGruopName)?.color;
						if (childValue) value += childValue;
						children.push([0, childValue, child.id, childText, childColor]);
					});
				}
				item.percent = parseFloat(value) / this._sum;
				const color = item.color || this._getSerie(isGruopName ? item.id : value, isGruopName)?.color;
				this._points.push([
					item.percent,
					value,
					item.id.toString(),
					text,
					color,
					{ items: children },
				]);
			},
			false
		);
		this._maxLevel = this._getMaxLevel();
		return this._points;
	}
	paint(width: number, height: number): SvgElement {
		const hasActiveSerie = !!this.config.treeSeries.find(item => item.active);
		if (!hasActiveSerie) return;

		const getChartAriaAttrs = cfg => ({
			"aria-label": `chart ${cfg.text || ""}`,
		});

		const { strokeWidth } = this.config;
		const defs = [];
		const bar = [];
		let svg = [];

		const outerStrokeWidth = this._maxLevel > 1 ? strokeWidth * 2 : strokeWidth;
		const restWidth = width - outerStrokeWidth;
		const restHeight = height - outerStrokeWidth;

		this._layout = {
			id: uid(),
			area: restWidth * restHeight,
			width: restWidth,
			height: restHeight,
			restArea: restWidth * restHeight,
			restWidth,
			restHeight,
			startX: outerStrokeWidth / 2,
			startY: outerStrokeWidth / 2,
			stroke: outerStrokeWidth,
		};

		let currentX = this._layout.startX;
		let currentY = this._layout.startY;

		if (this._layout.width >= this._layout.height) {
			this._layout["cols"] = [];
		} else {
			this._layout["rows"] = [];
		}

		this._points.sort((a, b) => b[0] - a[0]);
		this._points.forEach((item, index, array) => {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const [percent, value, id, text, color, children] = item;
			if (percent === 0) return;

			const [
				currentSector,
				linkText,
				clip,
				nextX,
				nextY,
				currentSectorObj,
				groupNameSector,
			] = this._drawBar(
				item,
				index,
				array,
				this._layout,
				currentX,
				currentY,
				!!(children && children.items?.length)
			);
			currentX = currentSectorObj[0];
			currentY = currentSectorObj[1];

			bar.push(sv("g", { id: id }, [currentSector, linkText, groupNameSector]));
			defs.push(clip);

			if (children && children.items?.length) {
				const childWidth = currentSectorObj[2] - strokeWidth;
				const childHeight = currentSectorObj[3] - strokeWidth;
				const childLayout = {
					id: uid(),
					parent: this._layout.id,
					area: childWidth * childHeight,
					width: childWidth,
					height: childHeight,
					restArea: childWidth * childHeight,
					restWidth: childWidth,
					restHeight: childHeight,
					startX: currentX + strokeWidth / 2,
					startY:
						currentY +
						strokeWidth / 2 +
						(this.config.direction === "asc" || !groupNameSector ? 0 : strokeWidth / 2),
					stroke: strokeWidth,
				};

				let childCurrentX = childLayout.startX;
				let childCurrentY = childLayout.startY;

				if (childLayout.width >= childLayout.height) {
					childLayout["cols"] = [];
				} else childLayout["rows"] = [];

				children.items.map(child => (child[0] = child[1] / item[1]));
				children.items.sort((a, b) => b[0] - a[0]);
				children.items.forEach((child, childInd, childArr) => {
					const childId = child[2];
					const [childSector, childLinkText, childClip, childNextX, childNextY] = this._drawBar(
						child,
						childInd,
						childArr,
						childLayout,
						childCurrentX,
						childCurrentY,
						false
					);
					bar.push(sv("g", { id: childId }, [childSector, childLinkText]));
					defs.push(childClip);
					childCurrentX = childNextX;
					childCurrentY = childNextY;
				});
			}
			currentX = nextX;
			currentY = nextY;
		});

		svg.push(sv("defs", defs));
		svg = svg.concat(bar);

		return sv(
			"g",
			{
				id: "seria" + this.config.id,
				...getChartAriaAttrs(this.config),
				tabindex: 0,
				transform:
					this.config.direction === "asc"
						? `rotate(180 ${this._layout.startX + this._layout.width / 2} ${this._layout.startY +
								this._layout.height / 2})`
						: "",
			},
			svg
		);
	}

	private _drawBar(
		item: PointData,
		index: number,
		array: PointData[],
		layout: ITreeLayout,
		currentX: number,
		currentY: number,
		isParent: boolean
	): [SvgElement, SvgElement, SvgElement, number, number, any, any] {
		const getPointAriaAttrs = (value, text, percent) => ({
			role: "graphics-symbol",
			"aria-roledescription": "piece of treeMap",
			"aria-label": `${text || ""}, ${value} (${roundToTwoNumAfterPoint(percent)}%)`,
		});

		const [percent, value, id, text, color] = item;
		const { stroke, showText, showTextTemplate } = this.config;
		const itemArea = percent * layout.area;
		const parentObj = this._getDeepParent(layout);
		const isGroup = isParent && percent > 0 && text;

		const maxX = layout.startX + layout.width - parentObj.restWidth;
		const maxY = layout.startY + layout.height - parentObj.restHeight;
		currentX = currentX > maxX ? maxX : currentX;
		currentY = currentY > maxY ? maxY : currentY;

		const [currentSectorObj, linkTextObj, nextX, nextY, parentId] = this._getBar(
			parentObj,
			itemArea,
			currentX,
			currentY,
			array,
			index,
			layout.area
		);

		if (currentSectorObj[2] < 0 || currentSectorObj[3] < 0) return;
		this._recountParentArea(itemArea, parentId, layout);

		const groupRect = isGroup
			? sv("rect", {
					x: currentSectorObj[0] + layout.stroke / 4,
					y:
						this.config.direction === "asc"
							? currentSectorObj[1] +
							  currentSectorObj[3] -
							  this._headerHeight -
							  layout.stroke / 2
							: currentSectorObj[1] + layout.stroke / 4,
					width: currentSectorObj[2] - layout.stroke / 2,
					height: this._headerHeight + layout.stroke / 4,
					stroke: stroke,
					"stroke-width": layout.stroke / 2,
					class: "treeMap-header",
					_key: id,
					onclick: () => this._toggleGroup(id),
			  })
			: null;
		const groupName = isGroup
			? sv(
					"text",
					{
						x: currentSectorObj[0] + currentSectorObj[2] / 2,
						y:
							this.config.direction === "asc"
								? currentSectorObj[1] +
								  currentSectorObj[3] -
								  this._headerHeight / 2 -
								  layout.stroke / 2
								: currentSectorObj[1] + this._headerHeight / 2 + layout.stroke / 2,
						class: "header-text",
						"aria-hidden": "true",
						transform:
							this.config.direction === "asc"
								? `rotate(180 ${currentSectorObj[0] +
										currentSectorObj[2] / 2} ${currentSectorObj[1] +
										currentSectorObj[3] -
										this._headerHeight / 2 -
										layout.stroke / 2})`
								: "",
					},
					[
						showTextTemplate
							? verticalCenteredText(showTextTemplate(text?.toString()))
							: verticalCenteredText(text?.toString()),
					]
			  )
			: null;
		const groupNameSector = isParent && percent > 0 && text ? sv("g", {}, [groupRect, groupName]) : null;

		const innerText = showTextTemplate ? showTextTemplate(text?.toString()) : text?.toString();
		const sizes = getSizesSVGText(innerText, { font: "normal 12px Roboto", lineHeight: 14 });
		const isFit = sizes[0] < currentSectorObj[2] - layout.stroke * 2;
		const clipId = uid();

		const clip =
			currentSectorObj[2] > layout.stroke * 2 && currentSectorObj[3] > layout.stroke * 2
				? sv(
						"clipPath",
						{
							id: clipId,
							"clip-rule": "nonzero",
						},
						[
							sv("rect", {
								x: currentSectorObj[0] + layout.stroke,
								y: currentSectorObj[1] + layout.stroke,
								width: currentSectorObj[2] - layout.stroke * 2,
								height: currentSectorObj[3] - layout.stroke * 2,
								fill: "none",
								stroke: "black",
							}),
						]
				  )
				: null;

		const currentSector = sv("rect", {
			...getPointAriaAttrs(value, text, percent),
			x: currentSectorObj[0],
			y: currentSectorObj[1],
			width: currentSectorObj[2],
			height: currentSectorObj[3],
			stroke: stroke,
			"stroke-width": layout.stroke,
			class: "chart treeMap",
			_key: id,
			fill: color,
			onclick: [this._handlers.onclick, item[1], item[2]],
			onmousemove:
				this._maxLevel > 1 && this._getDataLevel(id) === 1
					? ""
					: [this._handlers.onmousemove, array[index][2], this.id],
			onmouseleave:
				this._maxLevel > 1 && this._getDataLevel(id) === 1
					? ""
					: [this._handlers.onmouseleave, array[index][2], this.id],
		});

		const linkText = showText
			? sv(
					"text",
					{
						x: isFit ? linkTextObj[0] : currentSectorObj[0] + layout.stroke,
						y: linkTextObj[1],
						class: `treeMap-inner-value ${isFit ? "" : "start-text"}`,
						"aria-hidden": "true",
						"clip-path": `url("#${clipId}")`,
						transform:
							this.config.direction === "asc"
								? `rotate(180 ${linkTextObj[0]} ${linkTextObj[1]})`
								: "",
					},
					[verticalCenteredText(innerText)]
			  )
			: null;

		if (isParent && groupNameSector) {
			currentSectorObj[1] += this.config.direction === "asc" ? 0 : this._headerHeight;
			currentSectorObj[3] -= this._headerHeight + layout.stroke / 4;
		}
		return [currentSector, linkText, clip, nextX, nextY, currentSectorObj, groupNameSector];
	}

	private _getBar(
		parentObj: ITreeLayout,
		itemArea: number,
		currentX: number,
		currentY: number,
		arr: PointData[] | [],
		index: number,
		startArea: number
	): any {
		const checkNextItem = (
			itemObj: ITreeLayout,
			startIndex: number,
			nextIndex: number,
			direction: "rows" | "cols"
		) => {
			const sector = {
				area: 0,
				width: itemObj.width,
				height: itemObj.height,
			};
			for (let i = startIndex; i <= nextIndex; i++) {
				sector.area += arr[i][0] * startArea;
			}
			if (direction === "rows") {
				sector.width = sector.area / sector.height;
			} else {
				sector.height = sector.area / sector.width;
			}

			const newWidth = direction === "rows" ? sector.width : itemObj.area / sector.height;
			const newHeight = direction === "rows" ? itemObj.area / sector.width : sector.height;

			if (
				(newWidth / newHeight > this._aspectRatio || newHeight / newWidth > this._aspectRatio) &&
				arr[index][0] > 0.01 &&
				nextIndex !== this._points.length - 1
			) {
				return checkNextItem(itemObj, startIndex, nextIndex + 1, direction);
			} else {
				return [newWidth, newHeight, sector];
			}
		};

		let currentPiece;

		let nextX = currentX;
		let nextY = currentY;

		const direction = parentObj.restWidth > parentObj.restHeight ? "cols" : "rows";
		const crossDirection = direction === "cols" ? "rows" : "cols";

		let itemWidth = direction === "cols" ? itemArea / parentObj.restHeight : parentObj.restWidth;
		let itemHeight = direction === "cols" ? parentObj.restHeight : itemArea / parentObj.restWidth;

		if (
			(itemHeight / itemWidth > this._aspectRatio || itemWidth / itemHeight > this._aspectRatio) &&
			Math.round(itemArea - parentObj.restArea) !== 0 &&
			index < arr.length - 2 &&
			itemArea < parentObj.restArea
		) {
			let sector;
			currentPiece = {
				id: uid(),
				area: itemArea,
				width: itemWidth,
				height: itemHeight,
			};
			[currentPiece.width, currentPiece.height, sector] = checkNextItem(
				currentPiece,
				index,
				index + 1,
				crossDirection
			);
			itemWidth = currentPiece.width;
			itemHeight = currentPiece.height;
			sector = {
				id: uid(),
				area: sector.area,
				width: sector.width,
				height: sector.height,
				restArea: sector.area,
				restWidth: crossDirection === "cols" ? sector.width - itemWidth : sector.width,
				restHeight: crossDirection === "cols" ? sector.height : sector.height - itemHeight,
				[crossDirection]: [currentPiece],
			};
			currentPiece["parent"] = sector.id;

			if (!parentObj[direction] && !parentObj[crossDirection]) {
				parentObj[direction] = [sector];
				sector["parent"] = parentObj.id;
			} else if (parentObj[crossDirection]) {
				const parentId = uid();
				parentObj[crossDirection].push({
					id: parentId,
					parent: parentObj.id,
					area: parentObj.restArea,
					width: parentObj.restWidth,
					height: parentObj.restHeight,
					restArea: parentObj.restArea,
					restWidth:
						direction === "cols" ? parentObj.restWidth - sector.width : parentObj.restWidth,
					restHeight:
						direction === "cols" ? parentObj.restHeight : parentObj.restHeight - sector.height,
					[direction]: [sector],
				});
				sector["parent"] = parentId;
			} else {
				parentObj[direction].push(sector);
				sector["parent"] = parentObj.id;
			}

			if (direction === "cols") {
				parentObj.restWidth -= sector.width;
				nextY += itemHeight;
			} else {
				parentObj.restHeight -= sector.height;
				nextX += itemWidth;
			}
		} else {
			currentPiece = {
				id: uid(),
				area: itemArea,
				width: itemWidth,
				height: itemHeight,
			};
			if (parentObj[crossDirection] && parentObj.restArea.toFixed(2) > itemArea.toFixed(2)) {
				const parentId = uid();
				parentObj[crossDirection].push({
					id: parentId,
					parent: parentObj.id,
					area: parentObj.restArea,
					width: parentObj.restWidth,
					height: parentObj.restHeight,
					restArea: parentObj.restArea,
					restWidth: direction === "cols" ? parentObj.restWidth - itemWidth : parentObj.restWidth,
					restHeight:
						direction === "cols" ? parentObj.restHeight : parentObj.restHeight - itemHeight,
					[direction]: [currentPiece],
				});
				currentPiece["parent"] = parentId;
			} else {
				(parentObj["cols"] || parentObj["rows"]).push(currentPiece);
				currentPiece["parent"] = parentObj.id;
				if (direction === "cols") {
					parentObj.restWidth -= itemWidth;
				} else {
					parentObj.restHeight -= itemHeight;
				}
			}

			if (direction === "cols") {
				nextX += itemWidth;
				nextY = Math.round(parentObj.restArea - itemArea) === 0 ? nextY + itemHeight : nextY;
			} else {
				nextY += itemHeight;
				nextX = Math.round(parentObj.restArea - itemArea) === 0 ? nextX + itemWidth : nextX;
			}
		}

		const currentSectorObj = [currentX, currentY, currentPiece.width, currentPiece.height];
		const linkTextObj = [currentX + currentPiece.width / 2, currentY + currentPiece.height / 2];
		return [currentSectorObj, linkTextObj, nextX, nextY, currentPiece.parent];
	}
	protected _setDefaults(config: ITreeMapConfig): void {
		const defaults = {
			active: true,
			stroke: "#FFFFFF",
			strokeWidth: 2,
			showText: true,
			tooltip: true,
			paddings: 5,
			color: "none",
			fill: "none",
			direction: "desc",
		};

		this.config = { ...defaults, ...config } as ITreeMapConfig;
		this._headerHeight = 25;
		this._aspectRatio = 2.5;
		this._valueLocator = locator(config.value);
		this._textLocator = locator(config.text);
	}
	protected _defaultLocator(v) {
		return [this._valueLocator(v), this._textLocator(v)];
	}
	private _getSerie(value: number | string, isId?: boolean): ITreeSeries {
		const serie = this.config.treeSeries.find(item => {
			if (isId) {
				const parentId = this._data.getParent(value);
				return item.id === parentId || item.id === value;
			} else {
				return (
					(item.from && item.to && value >= item.from && value < item.to) ||
					(item.less && value < item.less) ||
					(item.greater && value > item.greater)
				);
			}
		});
		return serie;
	}
	private _getDataLevel(id: string): number {
		let level = 1;
		this._data.eachParent(id, () => {
			level++;
		});
		return level;
	}
	private _getMaxLevel(): number {
		let maxLevel = 1;
		this._data.forEach(el => {
			const level = this._getDataLevel(el.id);
			maxLevel = Math.max(level, maxLevel);
		});
		return maxLevel;
	}
	private _getDeepParent(obj: ITreeLayout): ITreeLayout {
		const child = (obj["cols"] || obj["rows"])?.find(
			item => item.restArea && Math.round(item.restArea) > 0
		);
		return child ? this._getDeepParent(child) : obj;
	}
	private _getLayoutObj(id: string, layout: ITreeLayout = this._layout): ITreeLayout {
		let obj;
		if (layout.id === id) {
			obj = layout;
		} else {
			const items = layout["cols"] || layout["rows"];
			if (!items || !items.length) return;
			for (let i = 0; i < items.length; i++) {
				obj = this._getLayoutObj(id, items[i]);
				if (obj) break;
			}
		}
		return obj;
	}
	private _recountParentArea(area: number, parentId: string, obj: ITreeLayout): void {
		const parentObj = this._getLayoutObj(parentId, obj);
		if (!parentObj || parentObj.id === obj.parent) return;
		parentObj.restArea -= area;
		if (parentObj.parent) this._recountParentArea(area, parentObj.parent, obj);
	}
	private _toggleGroup(id: string) {
		let activeSeries = 0;
		this.config.treeSeries.forEach(serie => {
			if (serie.active) activeSeries++;
		});
		this._data.eachChild(
			this._data.getRoot(),
			item => {
				if (item.id === id) return;
				if (this.config.legendType === "groupName") {
					if (activeSeries > 1) this._getSerie(item.id, true).active = true;
					this._events.fire(ChartEvents.toggleSeries, [item.id]);
				} else {
					item.$hidden = !item.$hidden;
					this._events.fire(DataEvents.change);
				}
			},
			false
		);
	}
}
